<template>
<div class="fill-place">
    <div class="person-info person-info--preview">
        <div class="person-info__content" :class="{_error: shouldShowError}">
            <div class="person-info__booking">
                {{place_label}}
            </div>
            <div class="person-info__service-icon">
                <vWarning v-if="shouldShowError" class="icon vicon-orange"/>
            </div>

            <div class="person-info__name">
                {{person_names.fio || '[Заполните ФИО]'}}
            </div>
            <div class="person-info__sign">
                {{person_names.born || '[Заполните Дату рождения]'}}, {{person_names.male || '[Заполните Пол]'}}
            </div>
            <div class="person-info__docs">
                <div class="person-info__docs-icon">
                    <component :is="docIcon" />
                </div>
                <div class="person-info__docs-type">{{person_names.doc_type || '[Заполните Документ]'}}</div>
                <div class="person-info__docs-num">{{person_names.doc_num  || '[Заполните Серию Номер]'}}</div>
            </div>

            <div class="person-info__extra" v-if="person_names.extraFields.length">
                <div 
                    class="person-info__extra-row" 
                    v-for="field in person_names.extraFields" 
                    :key="field.name"
                >
                    <span class="person-info__extra-label">{{field.title}}:</span>
                    <span class="person-info__extra-value">{{field.val}}</span>
                </div>
            </div>
        </div>
        <div class="person-info__errors" v-if="shouldShowError">
            {{person_names.isValid ? 'Корректно' : 'Вернитесь и заполните всю информацию'}}
        </div>
    </div>
</div>
</template>

<style lang="scss" src="./FillPlace.scss"></style>

<script setup>
import {unref, computed} from 'vue'
import useCabinsStore from '@/bookCabins.js'
import useTouristStore, {ttname_by_id, SID_CUSTOMER} from '@/tourist/tourist-store.js'
import {fio_person, fieldsInOrder} from '@/comp/useOrder.js'
import vWarning from '@/assets/vicons/seal-warning.vue'

import vEmblem from '@/assets/vicons/emblem_russia.vue'
import vChild from '@/assets/vicons/child.vue'
import vPlanet from '@/assets/vicons/planet.vue'
import vIdentificationCard from '@/assets/vicons/identification-card.vue'

const docIconMap = {
  ps: vEmblem,
  zps: vPlanet,
  svr: vChild,
  other: vIdentificationCard,
}

const docIcon = computed(() => {
    const {doc_id} = unref(person_names)
    return docIconMap[doc_id] || vEmblem
})

const bookCabins = useCabinsStore()
const tstore = useTouristStore()

const props = defineProps({
    place: Object,
    cabinId: Number,
    Cabin: Object,
    ages: Object,

    viewMode: {
        type: String,
        default: 'short',
    },
    validationMode: {
        type: String,
        default: 'lazy',
    },
})

const personItem = bookCabins.personItem(props.cabinId, props.place)

const shouldShowError = computed(() => {
    if (props.validationMode !== 'strict') return false
    return !person_names.value.isValid
})

const place_label = computed(() => {
    const cabinId = props.cabinId

    if (cabinId === bookCabins.customerCabinId) {
        return 'Заказчик; Взрослый'
    }

    const {pnum} = props.place
    let placeStr
    let typeStr

    if (pnum >= 0) {
        placeStr = `Место ${pnum + 1}`
        if (props.place.ttype === 'cr-adult') {
            typeStr = 'Взрослый'
        } else {
            const {since, till} = props.ages.child
            typeStr = `Ребенок ${since}–${till - 1}.99`
        }
    } else {
        placeStr = 'Инфант'
        const {since, till} = props.ages.infant
        typeStr = `${since}–${till - 1}.99`
    }

    return `${placeStr}; ${typeStr}`
})

const person_names = computed(() => {
    const {sid} = unref(personItem)

    let fio = ''
    let born = ''
    let male = ''
    let doc_type = ''
    let doc_id = ''
    let doc_num = ''
    let isValid = false
    let extraFields = []

    if (sid) {
        const person = tstore.person(sid)
        const isCust = sid === SID_CUSTOMER

        fio = fio_person(person, true)
        born = person.born.val && person.vest.tests.born.valid ? person.born.val : ''

        const maleOpt = person.male.options.find(ob => ob.id === person.male.val)
        male = maleOpt && person.male.val ? maleOpt.name.toLowerCase() : ''

        const docOpt = person.doc_type.options.find(ob => ob.id === person.doc_type.val)
        doc_type = docOpt && person.doc_type.val ? docOpt.name : ''
        doc_id = person.doc_type?.val

        doc_num = `${person.doc_serie.val || ''} ${person.doc_number.val || ''}`.trim()

        isValid = person.vest.isValid

        // Все остальные поля из FIELDS
        const FIELDS = fieldsInOrder(person)
        const ind = FIELDS.indexOf('doc_number')
        const FIELDS_AFTER = FIELDS.slice(ind+1)
//console.log('FIELDS_AFTER', FIELDS_AFTER, FIELDS)
        extraFields = FIELDS_AFTER
            .map(name => {
                const field = person[name]
                const title = field.title

                // Подставляем человекочитаемое значение
                let val = field.val
                if (field.options?.length) {
                    const opt = field.options.find(o => o.id === val)
                    if (opt) {
                        val = opt.name
                    }
                }
                if (!val) {
                    val = '[Заполните]'
                }

                return {name, title, val}
            })
    }

    return {
        fio,
        born,
        male,
        doc_type,
        doc_id,
        doc_num,
        isValid,
        extraFields,
    }
})

</script>